import { useEffect, useId, useMemo, useState } from "react";
import { t } from "ttag";

import { SettingsSection } from "metabase/admin/components/SettingsSection";
import {
  ACTIVE_STATUS,
  type ActiveStatus,
} from "metabase/admin/people/constants";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { usePageTitle } from "metabase/hooks/use-page-title";
import { useSelector } from "metabase/redux";
import { getUserIsAdmin } from "metabase/selectors/user";
import { Box, Group, Tabs, Title } from "metabase/ui";
import { useListTenantsQuery } from "metabase-enterprise/api";

import { EditUserStrategySettingsButton } from "../EditUserStrategySettingsButton";
import { TenantsDocsButton } from "../TenantsDocsButton";
import { TenantsListing } from "../components/TenantsListing";

export const TenantsListingApp = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  usePageTitle(t`Tenants`, { titleIndex: 1 });

  const isAdmin = useSelector(getUserIsAdmin);

  const [searchInputValue, setSearchInputValue] = useState("");
  const [status, setStatus] = useState<ActiveStatus>(ACTIVE_STATUS.active);

  const { isLoading, error, data } = useListTenantsQuery({ status: "all" });

  const tenants = useMemo(
    () =>
      data?.data.filter((tenant) =>
        status === ACTIVE_STATUS.active ? tenant.is_active : !tenant.is_active,
      ) ?? [],
    [data?.data, status],
  );

  const hasDeactivatedTenants = useMemo(
    () => data?.data.some((tenant) => !tenant.is_active),
    [data?.data],
  );

  const statusPanelId = useId();
  const getStatusTabId = (tab: ActiveStatus) => `${statusPanelId}-tab-${tab}`;
  const showStatusTabs = isAdmin && hasDeactivatedTenants;

  const handleTabChange = (tab: string | null) => {
    if (tab) {
      setStatus(tab as ActiveStatus);
    }
  };

  useEffect(() => {
    if (!hasDeactivatedTenants) {
      setStatus("active");
    }
  }, [hasDeactivatedTenants]);

  const hasNoTenants = data?.data?.length === 0;

  return (
    // Make the layout narrow when there are no tenants
    <Box maw={hasNoTenants ? "700px" : undefined} mx="auto">
      <Group justify="space-between" w="100%" mb="lg">
        <Title order={1}>{t`Tenants`}</Title>

        <Group gap="sm">
          <TenantsDocsButton />
          <EditUserStrategySettingsButton page="tenants" />
        </Group>
      </Group>

      {showStatusTabs && (
        <Tabs
          value={status}
          onChange={handleTabChange}
          pl="md"
          listBorder={false}
        >
          {/* Both tabs filter the one list below, so they control that
              region rather than Mantine's default per-value panel ids, which
              would reference elements that are never rendered
              (WCAG 4.1.2, aria_id_unique). */}
          <Tabs.List>
            <Tabs.Tab
              value={ACTIVE_STATUS.active}
              id={getStatusTabId(ACTIVE_STATUS.active)}
              aria-controls={statusPanelId}
            >
              {t`Active`}
            </Tabs.Tab>

            <Tabs.Tab
              value={ACTIVE_STATUS.deactivated}
              id={getStatusTabId(ACTIVE_STATUS.deactivated)}
              aria-controls={statusPanelId}
            >{t`Deactivated`}</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      )}

      <div
        id={statusPanelId}
        role={showStatusTabs ? "tabpanel" : undefined}
        aria-labelledby={showStatusTabs ? getStatusTabId(status) : undefined}
      >
        <SettingsSection>
          <LoadingAndErrorWrapper error={error} loading={isLoading}>
            <TenantsListing
              isAdmin={isAdmin}
              tenants={tenants}
              searchInputValue={searchInputValue}
              setSearchInputValue={setSearchInputValue}
              status={status}
              hasNoTenants={hasNoTenants}
            />
          </LoadingAndErrorWrapper>

          {children}
        </SettingsSection>
      </div>
    </Box>
  );
};
